import React, { useState, useEffect } from 'react';
import { Card, Stack, Button, Typography, Box } from '@mui/material';
import {
  LISTING_COLUMNS,
  LG_LISTING_ACTIONS,
} from '../../config/module-configs/lg-packages.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import {
  activeLGPackage,
  getLGPackageListData,
} from '../../services/channel-packager';
import { ACTIONS } from '../../config/const.config';
import useAccess from '../../hooks/access.hook';
import SnackbarInfo from '../common/snackbar-info.component';
import Channels from '../form-elements/channels.component';
import AddChannelLGPackager from '../app-content/add-channel-lg-packager.component';
import ConfirmPopup from '../common/confirm-popup.component';
import useFilteredActions from '../../hooks/filtered-action.hook';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListLGPackages = () => {
  const columns = LISTING_COLUMNS;
  const actions = useFilteredActions('channel_packager', LG_LISTING_ACTIONS);
  const { getSignal, isAbortError } = useAbortableRequest();

  const { hasOperationAccess } = useAccess();
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    packageUsedInChannels: '',
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      search: '',
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });

  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [selectedChannel, setSelectedChannel] = useState({});

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleActiveLGPackage = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    if (!doAction.data.isActive) {
      const payload = {
        id: doAction.data.id,
        value: 1,
        channelId: doAction.data.channelId,
      };

      activeLGPackage(payload)
        .then(() => {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: 'LG package has been activated successfully.',
          });
          handleActionCancel();
          handleRefreshData();
        })
        .catch((e) => {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message: e.response?.data?.message,
          });
          handleActionCancel();
          handleRefreshData();
        });
    } else {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message:
            'This LG package is already active. Please choose another package.',
        });
        handleActionCancel();
        handleRefreshData();
      }, 500);
    }
  };

  useEffect(() => {
    const signal = getSignal();

    const params = [];
    if (selectedChannel && selectedChannel?.id) {
      params.push(`channelId=${selectedChannel.id}`);
    }
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const filters = options.appliedFilters;
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getLGPackageListData(paramsQuery, signal)
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            packageUsedInChannels: res?.data?.packageUsedInChannels || '',
            error: false,
          });
        }
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          packageUsedInChannels: '',
          error: true,
        });
      });
  }, [options.reloadCounter, selectedChannel]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 0, py: 2 }}
      >
        <div>
          {hasOperationAccess('channel_packager', 'create') && (
            <>
              <Channels
                id="dr_channel"
                name="dr_channel"
                label="Select Channel"
                defaultValue={null}
                onChange={(v) => setSelectedChannel(v)}
                sx={{ width: '100%', my: 1 }}
              />
              <Typography variant="body2" color="secondary">
                Please select a channel to generate the LG package list.
              </Typography>
            </>
          )}
        </div>
        <div>
          <Box
            display="flex"
            gap={2}
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            {options.packageUsedInChannels && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexWrap: 'nowrap', width: '150px' }}
              >
                <Typography component="span" fontWeight="bold">
                  Summary: {options.packageUsedInChannels}
                </Typography>
              </Typography>
            )}
          </Box>
        </div>
      </Stack>
      {selectedChannel && selectedChannel?.id && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ px: 0, py: 2 }}
        >
          <div>
            {hasOperationAccess('channel_packager', 'create') && (
              <Stack direction="row">
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<Iconify icon="ic:round-add" />}
                  onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
                >
                  Upload New Package
                </Button>
                <Typography
                  variant="body2"
                  color="secondary"
                  ml={2}
                  mt={1}
                  sx={{ color: 'red' }}
                >
                  {`Please upload the file with the same name: ${selectedChannel.code}-webhosted.zip`}
                </Typography>
              </Stack>
            )}
          </div>
          <div>
            <Button
              color="inherit"
              variant="contained"
              startIcon={<Iconify icon="ic:twotone-refresh" />}
              sx={{ marginRight: 0 }}
              onClick={handleRefreshData}
              disabled={options.loading}
            >
              Refresh
            </Button>
          </div>
        </Stack>
      )}

      <Card>
        <ListData
          columns={
            selectedChannel?.id
              ? columns
              : columns.filter((item) => item.id !== 'isActive')
          }
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={selectedChannel?.id ? actions : []}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
        />
      </Card>

      {doAction.action === ACTIONS.CREATE.value && (
        <AddChannelLGPackager
          title={`Upload LG Package Zip: ${selectedChannel.title}`}
          channelId={selectedChannel?.id || 0}
          channelCode={selectedChannel?.code || ''}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {doAction.action === ACTIONS.MAKE_ACTIVE.value && (
        <ConfirmPopup
          title={`Active LG Package: ${selectedChannel.title}`}
          message="Would you like to activate the LG package?"
          noteText="After confirming, please wait for the process to complete and avoid closing this dialog."
          onCancel={handleActionCancel}
          onSuccess={handleActiveLGPackage}
          successButtonText="Active"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="active"
          infoText={{
            version: doAction.data.version,
            releaseNotes: doAction.data.notes,
          }}
        />
      )}
      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ListLGPackages.propTypes = {
  // value: PropTypes.string.isRequired,
};
export default ListLGPackages;
